(() => {
  'use strict';

  if (window.rkSemmelTours) {
    window.rkSemmelTours.init(document);
    return;
  }

  const tours = [
    { id: 5912, title: 'Unser Moment - Arena Tour 2027' },
    { id: 6023, title: 'Unser Sommermoment - Open Air 2027' }
  ];
  const requests = new Map();
  const dateFormat = new Intl.DateTimeFormat('de-DE', { timeZone: 'UTC' });
  const weekdayFormat = new Intl.DateTimeFormat('de-DE', { weekday: 'long', timeZone: 'UTC' });

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function parseDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T12:00:00Z`);
    return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? null : date;
  }

  function today() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const part = (type) => parts.find((item) => item.type === type).value;
    return `${part('year')}-${part('month')}-${part('day')}`;
  }

  function validate(data, tourId) {
    if (!Array.isArray(data)) throw new Error('Invalid concert response');
    for (const concert of data) {
      if (!concert || Number(concert.tourId) !== tourId || !Number.isInteger(concert.id) ||
          concert.name !== 'Roland Kaiser' || !parseDate(concert.date) ||
          !concert.venue || typeof concert.venue.name !== 'string' || typeof concert.venue.city !== 'string') {
        throw new Error('Invalid concert data');
      }
    }
    return data;
  }

  async function load(tourId) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`https://api.semmelcloud.de/api/concert/extended?tourId=${tourId}`, {
        headers: { Accept: 'application/json' }, credentials: 'omit', signal: controller.signal
      });
      if (!response.ok) throw new Error('Concert request failed');
      return validate(await response.json(), tourId);
    } finally {
      clearTimeout(timer);
    }
  }

  function ticketUrl(concert) {
    const info = concert.ticketInfo || {};
    const direct = info.ticketlink || info.ticketLink;
    if (direct) {
      try {
        const url = new URL(direct);
        if (url.protocol === 'https:' && !/[{}]/.test(url.href)) return url.href;
      } catch (_) {}
    }
    if (!/^\d+$/.test(String(info.ctsWebcode || '')) || typeof info.templateTicketUrl !== 'string') return null;
    const freeText = `${concert.name} ${concert.venue.city}`.normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const template = info.templateTicketUrl.replaceAll('{CTSWEBCODE}', info.ctsWebcode)
      .replaceAll('{FREETEXT}', freeText);
    try {
      const url = new URL(template);
      for (const [key, value] of [...url.searchParams]) {
        if (/[{}]/.test(value)) url.searchParams.delete(key);
      }
      if (url.protocol !== 'https:' || /[{}]/.test(decodeURIComponent(url.href))) return null;
      return url.href;
    } catch (_) {
      return null;
    }
  }

  function price(concert, labels) {
    const info = concert.ticketInfo || {};
    if (typeof info.minPrice !== 'number' || !Number.isFinite(info.minPrice) || info.minPrice <= 0 ||
        typeof info.currency !== 'string' || !/^[A-Z]{3}$/.test(info.currency)) return '';
    try {
      const amount = new Intl.NumberFormat('de-DE', { style: 'currency', currency: info.currency }).format(info.minPrice);
      return ` (${labels.from} ${amount})`;
    } catch (_) {
      return '';
    }
  }

  function row(concert, labels) {
    const node = element('div', 'tickets-termine-element');
    node.dataset.concertId = String(concert.id);
    const basics = element('div', 'ticket-basics');
    const datum = element('div', 'datum');
    let displayDate = dateFormat.format(parseDate(concert.date));
    if (parseDate(concert.dateTo) && concert.dateTo !== concert.date) displayDate += ` – ${dateFormat.format(parseDate(concert.dateTo))}`;
    datum.append(element('div', 'date', displayDate));
    const additional = element('div', 'additional-date');
    additional.append(element('div', 'time', weekdayFormat.format(parseDate(concert.date))));
    datum.append(additional);
    const description = element('div', 'description');
    description.append(element('div', 'location', concert.venue.name), element('div', 'ort', concert.venue.city));
    const previousDate = typeof concert.postponementDate === 'string' ? parseDate(concert.postponementDate.slice(0, 10)) : null;
    if (previousDate && concert.postponementDate.slice(0, 10) !== concert.date) {
      description.append(element('div', 'additional-date', `${labels.postponed} ${dateFormat.format(previousDate)}`));
    }
    basics.append(datum, description);
    node.append(basics);
    const cancelled = concert.isCancelled === true || Number(concert.isCancelled) === 1;
    const soldOut = concert.isSoldOut === true || Number(concert.isSoldOut) === 1;
    const url = ticketUrl(concert);
    if (cancelled || soldOut || url) {
      const overlay = element('div', 'ticket-overlay');
      if (cancelled || soldOut) {
        overlay.append(element('div', 'tickets-content', cancelled ? labels.cancelled : labels.soldOut));
      } else {
        const link = element('a', 'ticketLink');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.setAttribute('aria-label', `${labels.tickets}: ${concert.venue.city}, ${displayDate}`);
        const content = element('div', 'tickets-content', labels.tickets);
        content.append(element('span', 'ticketpreis', price(concert, labels)));
        link.append(content);
        overlay.append(link);
      }
      node.append(overlay);
    }
    return node;
  }

  async function render(root) {
    if (root.dataset.rkLoaded) return;
    root.dataset.rkLoaded = 'true';
    const labels = JSON.parse(root.querySelector('[data-rk-tour-labels]').textContent);
    const sections = tours.map((tour) => {
      const heading = element('h2', '', tour.title);
      const section = element('section', 'tickets-termine boxed');
      const list = element('div', 'tickets-termine-liste');
      section.setAttribute('aria-label', tour.title);
      section.append(list);
      return { tour, heading, section, list };
    });
    root.replaceChildren(...sections.flatMap(({ heading, section }) => [heading, section]));
    await Promise.all(sections.map(async ({ tour, list }) => {
      try {
        if (!requests.has(tour.id)) requests.set(tour.id, load(tour.id));
        const result = await requests.get(tour.id);
        const seen = new Set();
        const firstDate = today();
        const concerts = result.filter((concert) => {
          if ((parseDate(concert.dateTo) ? concert.dateTo : concert.date) < firstDate || seen.has(concert.id)) return false;
          seen.add(concert.id);
          return true;
        }).sort((a, b) => a.date.localeCompare(b.date) || String(a.begin || '').localeCompare(String(b.begin || '')) || a.id - b.id);
        const content = concerts.length ? concerts.map((concert) => row(concert, labels)) : [element('p', '', labels.empty)];
        list.replaceChildren(...content);
      } catch (_) {
        requests.delete(tour.id);
        const notice = element('p', '', labels.unavailable);
        notice.setAttribute('role', 'status');
        list.replaceChildren(notice);
      }
    }));
    root.setAttribute('aria-busy', 'false');
  }

  function init(scope) {
    scope.querySelectorAll('[data-rk-tour-dates]').forEach((root) => { void render(root); });
  }

  window.rkSemmelTours = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
